import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Globe, User, GraduationCap, Briefcase, Heart, Smartphone, Plane, Camera, Edit, Music, Dumbbell, Utensils, Film, Gamepad, BookOpen } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const INTEREST_ICON_RULES: { keywords: string[]; icon: typeof Heart }[] = [
    { keywords: ['voyage', 'travel'], icon: Plane },
    { keywords: ['photo'], icon: Camera },
    { keywords: ['ecriture', 'écriture', 'write'], icon: Edit },
    { keywords: ['lecture', 'livre', 'book', 'reading'], icon: BookOpen },
    { keywords: ['tech', 'info', 'ordinateur', 'code', 'programmation'], icon: Smartphone },
    { keywords: ['musique', 'music'], icon: Music },
    { keywords: ['sport', 'fitness', 'gym', 'football', 'course'], icon: Dumbbell },
    { keywords: ['jeu', 'jeux', 'gaming', 'game'], icon: Gamepad },
    { keywords: ['cuisine', 'cooking', 'food'], icon: Utensils },
    { keywords: ['cinema', 'cinéma', 'film', 'serie', 'série'], icon: Film },
];

function getInterestIcon(name: string) {
    const lower = (name || '').toLowerCase();
    const match = INTEREST_ICON_RULES.find((rule) => rule.keywords.some((k) => lower.includes(k)));
    return match ? match.icon : Heart;
}

const SkyBlueTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#4FC3F7';
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = '#ffffff', imageBorderColor = primaryColor } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+09-8209-283-038' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'Name@company.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Street Avenue 90' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'www.helpshared.com' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diammodoi nonummy nibb euismod od tincidunt ut laoreet dolore magna aliquam erat volutpat. Ut wisi enim ad minim veniam quis nostrud exerci tation doloju.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop'
        : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full relative overflow-hidden ${fontFamily}`} style={{ backgroundColor: backgroundColor, color: textColor }}>

            {/* Top Blue Waves Background */}
            <div className="absolute top-0 right-0 w-[70%] h-80 overflow-hidden">
                <svg viewBox="0 0 800 400" preserveAspectRatio="none" className="w-full h-full">
                    {/* Multiple blue wave layers */}
                    <path d="M0,100 Q200,50 400,100 T800,100 L800,0 L0,0 Z" fill={primaryColor} opacity="0.3" />
                    <path d="M0,150 Q200,100 400,150 T800,150 L800,0 L0,0 Z" fill={primaryColor} opacity="0.5" />
                    <path d="M0,200 Q200,150 400,200 T800,200 L800,0 L0,0 Z" fill={primaryColor} opacity="0.7" />
                </svg>
            </div>

            {/* Bottom Blue Wave */}
            <div className="absolute bottom-0 right-0 w-[65%] h-64 overflow-hidden">
                <svg viewBox="0 0 800 300" preserveAspectRatio="none" className="w-full h-full">
                    <path d="M0,100 Q200,50 400,100 T800,100 L800,300 L0,300 Z" fill={primaryColor} />
                </svg>
            </div>

            <div className="relative z-10 flex h-full">
                {/* Left Sidebar */}
                <div data-cv-sidebar className="w-[35%] p-10 pt-12 overflow-hidden">
                    {isContinuationPage ? (
                    <div className="mt-4 text-center">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.7 }}>
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider mt-2" style={{ color: textColor, opacity: 0.4 }}>{CV_LABELS.suite}</p>
                    </div>
                    ) : (
                    <>
                    {/* Profile Photo */}
                    {(photoSrc || showPlaceholders) && (
                    <div className="mb-8 flex justify-center">
                        <div className="w-44 h-44 rounded-full border-8 border-white overflow-hidden shadow-2xl" style={{ backgroundColor: primaryColor, borderColor: imageBorderColor === primaryColor ? 'white' : imageBorderColor }}>
                            {photoSrc ? (
                                <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                            ) : null}
                        </div>
                    </div>
                    )}

                    {/* Name and Title */}
                    <div className="text-center mb-5">
                        <h1 className="cv-fs-name text-5xl font-black uppercase leading-none mb-2">
                            {data.personalInfo.firstName || (showPlaceholders ? 'YERMIAH' : '')}<br />
                            {data.personalInfo.lastName || (showPlaceholders ? 'SERGE' : '')}
                        </h1>
                        <p className="cv-fs-profession text-lg font-bold" style={{ color: primaryColor }}>
                            {data.personalInfo.profession || (showPlaceholders ? 'Web Developer' : '')}
                        </p>
                    </div>

                    {/* Skills Section */}
                    <section className="mb-5">
                        <div className="flex items-center gap-3 mb-6">
                            <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="bar" className="mb-0" />
                        </div>
                        <div className="cv-fs-skills space-y-2 pl-2">
                            {(data.skills && data.skills.length > 0 ? data.skills.slice(0, 4) : showPlaceholders ? [
                                { name: 'PHP', level: 80 },
                                { name: 'HTML', level: 70 },
                                { name: 'Swift', level: 65 },
                                { name: 'Objective C', level: 60 }
                            ] : []).map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center justify-between"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <span className="text-sm font-bold">{skill.name}</span>
                                    {/* Toggle-like indicator */}
                                    <div className="relative w-16 h-7 bg-slate-200 rounded-full shadow-inner">
                                        <div
                                            className="absolute top-1 left-1 h-5 rounded-full shadow-md transition-all"
                                            style={{ width: `${skill.level * 0.5}%`, backgroundColor: primaryColor }}
                                        >
                                            <div className="absolute right-0 w-5 h-5 bg-white rounded-full shadow-md border-2" style={{ borderColor: primaryColor }}></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Language Section */}
                    <section className="mb-5">
                        <div className="flex items-center gap-3 mb-6">
                            <SectionHeading title={CV_LABELS.languages} isContinuation={langContinued} primaryColor={primaryColor} variant="bar" className="mb-0" />
                        </div>
                        <div className="cv-fs-languages space-y-4 pl-2">
                            {(data.languages && data.languages.length > 0 ? data.languages.slice(0, 4) : showPlaceholders ? [
                                { name: 'ENGLISH', level: 100 },
                                { name: 'SPANISH', level: 80 },
                                { name: 'GERMANY', level: 60 },
                                { name: 'CHINESE', level: 60 }
                            ] : []).map((lang, i) => (
                                <div
                                    key={lang.id || lang.name || i}
                                    className="flex items-center justify-between"
                                    data-cv-block
                                    data-cv-item-id={lang.id || undefined}
                                >
                                    <span className="text-sm font-bold uppercase">{lang.name}</span>
                                    <div className="flex gap-2">
                                        {[1, 2, 3, 4, 5].map((dot) => (
                                            <div
                                                key={dot}
                                                className={`w-3 h-3 rounded-full ${dot <= Math.ceil(lang.level / 20)
                                                    ? ''
                                                    : 'bg-slate-300'
                                                    }`}
                                                style={{ backgroundColor: dot <= Math.ceil(lang.level / 20) ? primaryColor : undefined }}
                                            ></div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Interest Section */}
                    {((data.interests && data.interests.length > 0) || showPlaceholders) && (
                    <section>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                                <Heart className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-xl font-black uppercase">{CV_LABELS.interests}</h2>
                        </div>
                        <div className="cv-fs-interests grid grid-cols-4 gap-4 pl-2">
                            {(data.interests && data.interests.length > 0
                                ? data.interests
                                : showPlaceholders
                                    ? [{ name: 'Tech' }, { name: 'Voyage' }, { name: 'Photo' }, { name: 'Écriture' }]
                                    : []
                            ).map((interestItem, i) => {
                                const Icon = getInterestIcon(interestItem.name);
                                return (
                                    <div
                                        key={('id' in interestItem && interestItem.id) || i}
                                        className="flex flex-col items-center"
                                        {...('id' in interestItem ? { 'data-cv-block': true, 'data-cv-item-id': interestItem.id || undefined } : {})}
                                    >
                                        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 shadow-md" style={{ backgroundColor: primaryColor }}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <span className="text-[9px] font-bold text-center">{interestItem.name}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                    )}

                    {/* Footer Website */}
                    <div className="absolute bottom-6 left-10 right-10 text-center">
                        <p className="text-xs font-bold text-slate-600">
                            {website}
                        </p>
                    </div>
                    </>
                    )}
                </div>

                {/* Right Content */}
                <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12 pt-32" style={{ backgroundColor: backgroundColor.toLowerCase() === '#ffffff' ? '#ffffff' : backgroundColor }}>

                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                    {/* Contact Section */}
                    {(phone || email || address || website || showPlaceholders) && (
                    <section className="mb-5">
                        <h2 className="text-2xl font-black mb-6">{CV_LABELS.contact}</h2>
                        <div className="cv-fs-contact grid grid-cols-2 gap-6">
                            {email && (
                            <div className="flex items-center gap-3">
                                <Mail className="w-5 h-5" style={{ color: primaryColor }} />
                                <span className="text-sm break-all">{email}</span>
                            </div>
                            )}
                            {phone && (
                            <div className="flex items-center gap-3">
                                <Phone className="w-5 h-5" style={{ color: primaryColor }} />
                                <span className="text-sm">{phone}</span>
                            </div>
                            )}
                            {address && (
                            <div className="flex items-center gap-3">
                                <MapPin className="w-5 h-5" style={{ color: primaryColor }} />
                                <span className="text-sm" style={{ color: textColor }}>{address}</span>
                            </div>
                            )}
                            {website && (
                            <div className="flex items-center gap-3">
                                <Globe className="w-5 h-5" style={{ color: primaryColor }} />
                                <span className="text-sm">{website}</span>
                            </div>
                            )}
                        </div>
                    </section>
                    )}

                    {/* Profile Section */}
                    {summary && (
                    <section className="mb-5">
                        <div className="inline-flex items-center gap-3 text-white px-8 py-3 rounded-full mb-6 shadow-lg" style={{ backgroundColor: primaryColor }}>
                            <User className="w-5 h-5" />
                            <h2 className="text-base font-black uppercase tracking-wider">{CV_LABELS.profile}</h2>
                        </div>
                        <p className="cv-fs-summary text-sm leading-relaxed mb-4" style={{ color: textColor, opacity: 0.8 }}>
                            {summary}
                        </p>
                    </section>
                    )}

                    {/* Education Section */}
                    <section className="mb-5">
                        <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="pill" />
                        <div className="cv-fs-education space-y-6">
                            {data.education.length > 0 ? data.education.map((edu, i) => (
                                <div
                                    key={edu.id || i}
                                    className="flex gap-4"
                                    data-cv-block
                                    data-cv-item-id={edu.id || undefined}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                                        {i < data.education.length - 1 && <div className="w-0.5 flex-1 mt-2" style={{ backgroundColor: primaryColor }}></div>}
                                    </div>
                                    <div className="flex-1 pb-6">
                                        <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>{edu.institution}</h3>
                                        <p className="text-sm leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>
                                            {edu.degree} in {edu.field}. {edu.description || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diammodoi nonummy nibb euismod.'}
                                        </p>
                                        <p className="text-xs font-bold mt-2" style={{ color: textColor, opacity: 0.5 }}>
                                            {edu.startDate.slice(0, 4)} – {edu.isCurrent ? CV_LABELS.present : edu.endDate.slice(0, 4)}
                                        </p>

                                    </div>
                                </div>
                            )) : showPlaceholders ? (
                                <>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Local Programmer College</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diammodoi nonummy nibb euismod.
                                            </p>
                                            <p className="text-xs text-slate-500 font-bold mt-2">2012 – 2016</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>International Coding University</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diammodoi nonummy nibb euismod.
                                            </p>
                                            <p className="text-xs text-slate-500 font-bold mt-2">2012 – 2016</p>
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </section>

                    {/* Work Experience Section */}
                    <section>
                        <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="pill" />
                        <div className="cv-fs-experience space-y-6">
                            {data.experience.length > 0 ? data.experience.map((exp, i) => (
                                <div
                                    key={exp.id || i}
                                    className="flex gap-4"
                                    data-cv-block
                                    data-cv-item-id={exp.id || undefined}
                                >
                                    <div className="flex flex-col items-center">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryColor }}></div>
                                        {i < data.experience.length - 1 && <div className="w-0.5 flex-1 mt-2" style={{ backgroundColor: primaryColor }}></div>}
                                    </div>
                                    <div className="flex-1 pb-6">
                                        <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>{exp.position} ( {exp.company} )</h3>
                                        <p className="text-sm leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>{exp.description}</p>
                                        <p className="text-xs font-bold mt-2" style={{ color: textColor, opacity: 0.5 }}>
                                            {exp.startDate} – {frPresent(exp.isCurrent, exp.endDate)}
                                        </p>
                                    </div>

                                </div>
                            )) : showPlaceholders ? (
                                <>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Web Developer ( Company 01 )</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diammodoi nonummy nibb euismod tincidunt.
                                            </p>
                                            <p className="text-xs text-slate-500 font-bold mt-2">2012 – 2016</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="w-3 h-3 rounded-full mt-1" style={{ backgroundColor: primaryColor }}></div>
                                        <div>
                                            <h3 className="text-base font-black mb-1" style={{ color: primaryColor }}>Senior Web Developer ( Company 02 )</h3>
                                            <p className="text-sm text-slate-600 leading-relaxed">
                                                Lorem ipsum dolor sit amet, consectetur adipiscing elit sed diammodoi nonummy nibb euismod tincidunt.
                                            </p>
                                            <p className="text-xs text-slate-500 font-bold mt-2">2012 – 2016</p>
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default SkyBlueTemplate;
